import React from "react";
import { useDebounce } from "@tiny/utils";
import {
  usePutCampaignFindFilterQuery,
  useGetCampaignByIdQuery,
} from "../../../../app/useGeneratedApi";
import { ActivityOption } from "../../TriggerForm.types";
import { FORM_CONSTANTS } from "../SelectRules.constants";
import { UseFormSetValue } from "react-hook-form";
import { FormValues } from "../SelectRules.hooks";

interface UseActivityDataParams {
  node: any;
  isTinyEmail: boolean;
  isTinyAlbert: boolean;
  setValue: UseFormSetValue<FormValues>;
  ruleType: string;
}

export const useActivityData = ({
  node,
  setValue,
  ruleType,
}: UseActivityDataParams) => {
  const [activitySearchText, setActivitySearchText] = React.useState("");
  const [currentPage, setCurrentPage] = React.useState(0);
  const [allActivities, setAllActivities] = React.useState<ActivityOption[]>(
    []
  );
  const [hasMoreResults, setHasMoreResults] = React.useState(true);
  const [isLoadingMore, setIsLoadingMore] = React.useState(false);

  const searchDebounced = useDebounce(activitySearchText);

  const { data: campaignResponse, isFetching: isCampaignLoading } =
    usePutCampaignFindFilterQuery(
      {
        searchTextName: searchDebounced,
      },
      {
        page: currentPage,
        size: FORM_CONSTANTS.PAGE_SIZE,
        sort: ["updatedAt,DESC"],
      },
      {
        keepPreviousData: true,
        enabled: ruleType === FORM_CONSTANTS.RULE_TYPES.MARKETING_ACTIVITY,
      }
    );

  const { data: selectedCampaign } = useGetCampaignByIdQuery(
    node?.data?.activityId,
    {
      enabled: !!(
        node?.data?.activityId &&
        ruleType === FORM_CONSTANTS.RULE_TYPES.MARKETING_ACTIVITY
      ),
    }
  );

  React.useEffect(() => {
    if (
      !campaignResponse ||
      ruleType !== FORM_CONSTANTS.RULE_TYPES.MARKETING_ACTIVITY
    )
      return;

    const content = campaignResponse?.content ?? [];
    const newActivities = content.map((campaign: any) => ({
      id: campaign.id || "",
      name: campaign.name || "",
      type: "CAMPAIGN",
    })) as ActivityOption[];

    setAllActivities((prev) => {
      const combined = [...prev, ...newActivities];
      const uniqueActivities = new Map(
        combined.map((activity) => [activity.id, activity])
      );
      return Array.from(uniqueActivities.values());
    });

    if (campaignResponse?.totalElements !== undefined) {
      setHasMoreResults(
        allActivities.length + newActivities.length <
          campaignResponse.totalElements
      );
    }
    setIsLoadingMore(false);
  }, [campaignResponse, ruleType]);

  React.useEffect(() => {
    setAllActivities([]);
    setCurrentPage(0);
    setHasMoreResults(true);
  }, [searchDebounced]);

  React.useEffect(() => {
    setCurrentPage(0);
    setAllActivities([]);
    setHasMoreResults(true);
    setActivitySearchText("");
  }, [ruleType]);

  React.useEffect(() => {
    if (
      ruleType === FORM_CONSTANTS.RULE_TYPES.MARKETING_ACTIVITY &&
      selectedCampaign
    ) {
      const activityOption: ActivityOption = {
        id: selectedCampaign.id || "",
        name: selectedCampaign.name || "",
        type: "CAMPAIGN",
      };

      setValue("activityId", activityOption?.id);

      setAllActivities((prev) => {
        const exists = prev.some(
          (activity) => activity.id === activityOption.id
        );
        if (!exists) {
          return [activityOption, ...prev];
        }
        return prev;
      });
    }
  }, [selectedCampaign, ruleType, setValue]);

  const handleActivitySearch = (searchText: string) => {
    setActivitySearchText(searchText);
  };

  const loadMoreActivities = () => {
    if (hasMoreResults && !isLoadingMore && !isCampaignLoading) {
      setIsLoadingMore(true);
      setCurrentPage((prevPage) => prevPage + 1);
    }
  };

  const isLoading = isCampaignLoading;
  return {
    activities: allActivities,
    handleActivitySearch,
    loadMoreActivities,
    isLoadingMore,
    hasMoreResults,
    isLoading,
  };
};
